import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Download, PlayAudioIcon, PauseIcon } from '../../../../shared/svg';
import toaster from '../../../../shared/toaster';
import { accessibleOnClick } from '../../../../shared/utils';
import { RootState } from '../../../../store/root-reducer';
import { OverlayTooltip } from '../../../../shared/design-system/components/overlay';

/** Save a blob as a file download (same-tab, no navigation). */
function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Load remote audio as a Blob so we can attach a filename (avoids opening the URL in a new tab).
 * Tries fetch (CORS), then XHR, then axios — signed URLs / CDNs vary.
 */
async function fetchRecordingBlob(url: string): Promise<Blob | null> {
  const tryFetchOnce = async (init: RequestInit): Promise<Blob | null> => {
    try {
      const response = await fetch(url, init);
      if (!response.ok) return null;
      const nextBlob = await response.blob();
      return nextBlob && nextBlob.size > 0 ? nextBlob : null;
    } catch {
      return null;
    }
  };

  let blob = await tryFetchOnce({
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  });
  if (blob) return blob;

  blob = await tryFetchOnce({
    mode: 'cors',
    credentials: 'include',
    cache: 'no-store',
  });
  if (blob) return blob;

  const xhrGetBlob = (withCredentials: boolean): Promise<Blob | null> =>
    new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.withCredentials = withCredentials;
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300 && xhr.response?.size > 0) {
          resolve(xhr.response);
        } else {
          resolve(null);
        }
      };
      xhr.onerror = () => resolve(null);
      xhr.send();
    });

  blob = await xhrGetBlob(false);
  if (blob) return blob;

  blob = await xhrGetBlob(true);
  if (blob) return blob;

  const tryAxiosOnce = async (
    withCredentials: boolean,
  ): Promise<Blob | null> => {
    try {
      const res = await axios.get(url, {
        responseType: 'blob',
        withCredentials,
      });
      const data = res.data as Blob;
      return data && data.size > 0 ? data : null;
    } catch {
      return null;
    }
  };

  blob = await tryAxiosOnce(false);
  if (blob) return blob;

  blob = await tryAxiosOnce(true);
  return blob || null;
}

export interface IAudioPlayerProps {
  audioSrc?: string | null;
  className?: string;
  onRequestClose?: () => void;
  closeBoundaryRef?: React.RefObject<HTMLElement>;
  showPlaybackSpinner?: boolean;
  onPlayWithoutSrc?: () => void;
  /**
   * When `downloadAttachment` is `recording` and there is no `audioSrc` yet, called on download click
   * so the parent can fetch a signed URL (same idea as `onPlayWithoutSrc`).
   */
  onDownloadWithoutSrc?: () => void;
  /**
   * When the parent is fetching a signed URL for download only, set true while the request is in flight.
   * Keeps download wait state in sync without using `showPlaybackSpinner` (play button).
   */
  recordingDownloadUrlFetchPending?: boolean;
  autoPlayWhenSrcAvailable?: boolean;
  onAutoPlayCompleted?: () => void;
  defaultDuration?: number;
  pauseOnScroll?: boolean;
  /** When true, shows a download control (behavior depends on `downloadAttachment`). */
  showTranscriptDownload?: boolean;
  /**
   * `transcript-text` — save `transcript` as a `.txt` file.
   * `recording` — fetch `audioSrc` and save as `recordingDownloadFileName` (call recording audio).
   */
  downloadAttachment?: 'transcript-text' | 'recording';
  /** Plain-text transcript when `downloadAttachment` is `transcript-text`. */
  transcript?: string | null;
  /** File name for transcript download (default: `call-transcript.txt`). */
  transcriptDownloadFileName?: string;
  /** File name for recording download when `downloadAttachment` is `recording` (default: `call-recording.mp3`). */
  recordingDownloadFileName?: string;
  /** Called after a successful download (transcript or recording). */
  onTranscriptDownload?: () => void;
}

const AudioPlayer: React.FC<IAudioPlayerProps> = ({
  audioSrc,
  className = '',
  onRequestClose,
  closeBoundaryRef,
  showPlaybackSpinner = false,
  onPlayWithoutSrc,
  onDownloadWithoutSrc,
  recordingDownloadUrlFetchPending,
  autoPlayWhenSrcAvailable,
  onAutoPlayCompleted,
  defaultDuration = 0,
  pauseOnScroll = true,
  showTranscriptDownload = false,
  downloadAttachment = 'transcript-text',
  transcript = null,
  transcriptDownloadFileName = 'call-transcript.txt',
  recordingDownloadFileName = 'call-recording.mp3',
  onTranscriptDownload,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0); // default 3 minutes in seconds (03:00)a
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [shouldAutoPlayOnSrc, setShouldAutoPlayOnSrc] = useState(
    Boolean(autoPlayWhenSrcAvailable),
  );
  const [
    isRecordingDownloadInProgress,
    setIsRecordingDownloadInProgress,
  ] = useState(false);
  const recordingDownloadInFlightRef = useRef(false);
  const [pendingDownloadAfterUrl, setPendingDownloadAfterUrl] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const durationSetRef = useRef(false);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  const currentCallManagementState = useSelector(
    (state: RootState) => state?.dialer?.currentCallManagementState,
  );
  const disableAudioPlayer =
    currentCallManagementState?.callStatus === 'ringing' ||
    (currentCallManagementState?.callTwilioStatus === 'connected' &&
      currentCallManagementState?.callStatus === 'answered');

  const handlePlayPause = useCallback(() => {
    // If no audio source is available yet, delegate to optional callback.
    if (!audioRef.current || !audioSrc) {
      // Remember that user explicitly requested playback so we can
      // auto-start once when the source becomes available.
      setShouldAutoPlayOnSrc(true);
      if (onPlayWithoutSrc) {
        onPlayWithoutSrc();
      }
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [audioSrc, isPlaying, onPlayWithoutSrc]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (progressRef.current && audioRef.current) {
        const rect = progressRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = x / rect.width;
        const newTime = percentage * duration;
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    },
    [duration],
  );

  const transcriptTrimmed = transcript?.trim() ?? '';
  const canDownloadTranscriptText =
    downloadAttachment === 'transcript-text' &&
    showTranscriptDownload &&
    transcriptTrimmed.length > 0;
  const canDownloadRecording =
    downloadAttachment === 'recording' &&
    showTranscriptDownload &&
    (!!audioSrc || !!onDownloadWithoutSrc);

  const handleDownloadTranscript = useCallback(() => {
    if (!transcriptTrimmed) return;
    const blob = new Blob([transcriptTrimmed], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = transcriptDownloadFileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    onTranscriptDownload?.();
  }, [onTranscriptDownload, transcriptDownloadFileName, transcriptTrimmed]);

  const handleDownloadRecording = useCallback(async () => {
    if (!audioSrc || recordingDownloadInFlightRef.current) return;
    recordingDownloadInFlightRef.current = true;
    setIsRecordingDownloadInProgress(true);
    try {
      const blob = await fetchRecordingBlob(audioSrc);
      if (blob && blob.size > 0) {
        saveBlobAsFile(blob, recordingDownloadFileName);
        onTranscriptDownload?.();
        return;
      }
      toaster.error(
        'Unable to download the recording. Check your connection or try again.',
      );
    } finally {
      recordingDownloadInFlightRef.current = false;
      setIsRecordingDownloadInProgress(false);
    }
  }, [audioSrc, onTranscriptDownload, recordingDownloadFileName]);

  const handleDownloadClick = useCallback(() => {
    if (downloadAttachment === 'transcript-text') {
      handleDownloadTranscript();
      return;
    }
    if (isRecordingDownloadInProgress || pendingDownloadAfterUrl) {
      return;
    }
    if (!audioSrc) {
      if (onDownloadWithoutSrc) {
        setPendingDownloadAfterUrl(true);
        onDownloadWithoutSrc();
      } else {
        toaster.error('Recording is not available to download yet.');
      }
      return;
    }
    // eslint-disable-next-line no-void
    void handleDownloadRecording();
  }, [
    downloadAttachment,
    handleDownloadRecording,
    handleDownloadTranscript,
    audioSrc,
    isRecordingDownloadInProgress,
    onDownloadWithoutSrc,
    pendingDownloadAfterUrl,
  ]);

  const showRecordingDownloadSpinner =
    downloadAttachment === 'recording' &&
    (isRecordingDownloadInProgress || pendingDownloadAfterUrl);

  let downloadButtonTitle = 'No transcript available';
  if (downloadAttachment === 'recording') {
    downloadButtonTitle = 'Recording not available';
    if (isRecordingDownloadInProgress) {
      downloadButtonTitle = 'Download is in progress';
    } else if (pendingDownloadAfterUrl) {
      downloadButtonTitle = 'Preparing download…';
    } else if (canDownloadRecording) {
      downloadButtonTitle = 'Download recording';
    }
  } else if (canDownloadTranscriptText) {
    downloadButtonTitle = 'Download transcript';
  }

  const downloadButtonClassName = [
    'audio-player-download-button',
    showRecordingDownloadSpinner && 'audio-player-download-button--busy',
  ]
    .filter(Boolean)
    .join(' ');

  const DownloadButton = () => (
    <button
      type="button"
      className={downloadButtonClassName}
      {...accessibleOnClick(handleDownloadClick)}
      disabled={
        downloadAttachment === 'recording'
          ? !canDownloadRecording
          : !canDownloadTranscriptText
      }
      title={downloadButtonTitle}
    >
      {showRecordingDownloadSpinner ? (
        <Spinner
          animation="border"
          size="sm"
          className="audio-player-download-spinner"
        />
      ) : (
        <Download width={16} height={16} />
      )}
    </button>
  );

  const handleSpeedChange = useCallback(() => {
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  }, [playbackSpeed]);

  const pauseAudio = useCallback(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);

  // Reset player state when audio source changes
  useEffect(() => {
    durationSetRef.current = false; // Reset flag when audio source changes
    if (!audioRef.current || !audioSrc) {
      setCurrentTime(0);
      setDuration(defaultDuration ?? 0); // Reset to default when no audio source
      setPlaybackSpeed(1); // Reset speed to 1x
      setIsPlaying(false);
      return;
    }

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    audioRef.current.playbackRate = 1; // Reset audio element speed to 1x
    setCurrentTime(0);
    setPlaybackSpeed(1); // Reset speed state to 1x to keep UI in sync
    setIsPlaying(false);
    setDuration(defaultDuration ?? 0);
    // Don't reset duration here - let onLoadedMetadata set it from the new audio
  }, [audioSrc, defaultDuration]);

  useEffect(() => {
    recordingDownloadInFlightRef.current = false;
    setIsRecordingDownloadInProgress(false);
    if (!audioSrc) {
      setPendingDownloadAfterUrl(false);
    }
  }, [audioSrc]);

  useEffect(() => {
    if (!pendingDownloadAfterUrl || !audioSrc) return;
    setPendingDownloadAfterUrl(false);
    // eslint-disable-next-line no-void
    void handleDownloadRecording();
  }, [pendingDownloadAfterUrl, audioSrc, handleDownloadRecording]);

  /**
   * Download-only URL fetch ended without `audioSrc` (failure or empty) — parent sets
   * `recordingDownloadUrlFetchPending` false; clear local wait state.
   */
  useEffect(() => {
    if (recordingDownloadUrlFetchPending === undefined) return;
    if (!pendingDownloadAfterUrl || audioSrc) return;
    if (recordingDownloadUrlFetchPending) return;
    setPendingDownloadAfterUrl(false);
  }, [pendingDownloadAfterUrl, audioSrc, recordingDownloadUrlFetchPending]);

  // Sync auto-play flag when prop changes
  useEffect(() => {
    if (autoPlayWhenSrcAvailable) {
      setShouldAutoPlayOnSrc(true);
    }
  }, [autoPlayWhenSrcAvailable]);

  useEffect(() => {
    if (!pauseOnScroll) return;
    const handleScroll = (event: Event) => {
      pauseAudio();
      const boundary = closeBoundaryRef?.current;
      const target = event.target as HTMLElement | null;
      if (boundary && target && boundary.contains(target)) {
        return;
      }
      onRequestClose?.();
    };

    window.addEventListener('scroll', handleScroll, true);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [closeBoundaryRef, onRequestClose, pauseAudio]);

  // Pause audio on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const boundary = closeBoundaryRef?.current || containerRef.current;
      if (boundary && !boundary.contains(event.target as Node)) {
        pauseAudio();
        onRequestClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeBoundaryRef, onRequestClose, pauseAudio]);

  let downloadTooltipText = 'Download';
  if (pendingDownloadAfterUrl && !isRecordingDownloadInProgress) {
    downloadTooltipText = 'Preparing download…';
  } else if (isRecordingDownloadInProgress) {
    downloadTooltipText = 'Download is in progress';
  }

  return (
    <>
      {/* Hidden native audio element that powers the custom player */}
      {audioSrc && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="metadata"
          onTimeUpdate={(event) => {
            const element = event.currentTarget;
            setCurrentTime(element.currentTime);
          }}
          onLoadedMetadata={(event) => {
            const element = event.currentTarget;
            const newDuration = element.duration;
            if (
              Number.isFinite(newDuration) &&
              !Number.isNaN(newDuration) &&
              newDuration > 0
            ) {
              setDuration(newDuration);
              durationSetRef.current = true;
            }
          }}
          onCanPlay={(event) => {
            const element = event.currentTarget;

            if (!durationSetRef.current) {
              const newDuration = element.duration;
              if (
                Number.isFinite(newDuration) &&
                !Number.isNaN(newDuration) &&
                newDuration > 0
              ) {
                setDuration(newDuration);
                durationSetRef.current = true;
              }
            }

            // If auto-play was requested (either via user click before src,
            // or via autoPlayWhenSrcAvailable), start playback once audio is ready.
            if (shouldAutoPlayOnSrc && !isPlaying) {
              const playPromise = element.play();
              if (
                playPromise &&
                typeof (playPromise as any).then === 'function'
              ) {
                (playPromise as Promise<void>)
                  .then(() => {
                    setIsPlaying(true);
                    setShouldAutoPlayOnSrc(false);
                    onAutoPlayCompleted?.();
                  })
                  .catch(() => {
                    setIsPlaying(false);
                    setShouldAutoPlayOnSrc(false);
                    onAutoPlayCompleted?.();
                  });
              }
            }
          }}
          onEnded={() => {
            setCurrentTime(0);
            setIsPlaying(false);
          }}
          aria-hidden="true"
        />
      )}
      <div
        ref={containerRef}
        className={`audio-player-bar ${className}`}
        {...accessibleOnClick((e) => e.stopPropagation())}
      >
        <button
          className={`audio-player-play-button ${isPlaying ? 'active' : ''}`}
          type="button"
          {...accessibleOnClick(handlePlayPause)}
          disabled={(!audioSrc && !onPlayWithoutSrc) || disableAudioPlayer}
        >
          {showPlaybackSpinner && (
            <Spinner
              animation="border"
              size="sm"
              className="audio-player-playback-spinner"
            />
          )}
          {!showPlaybackSpinner && isPlaying && (
            <PauseIcon
              width={16}
              height={16}
              className="audio-player-pause-audio-icon"
            />
          )}
          {!showPlaybackSpinner && !isPlaying && (
            <PlayAudioIcon
              width={16}
              height={16}
              className="audio-player-play-audio-icon"
            />
          )}
        </button>
        <div
          className="audio-player-progress"
          ref={progressRef}
          {...accessibleOnClick(handleProgressClick)}
        >
          <div
            className="audio-player-progress-filled"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
          {/* <div
            className="audio-player-progress-thumb"
            style={{ left: `${(currentTime / duration) * 100}%` }}
          /> */}
        </div>
        <span className="audio-player-time">
          {formatTime(Math.max(0, duration - currentTime))}
        </span>
        <button
          className="audio-player-speed"
          type="button"
          {...accessibleOnClick(handleSpeedChange)}
          disabled={!audioSrc}
        >
          {playbackSpeed}x
        </button>
        {showTranscriptDownload &&
          (audioSrc ||
          (downloadAttachment === 'recording' && onDownloadWithoutSrc) ? (
            <OverlayTooltip text={downloadTooltipText} rePositionOnChange>
              <span className="audio-player-download-tooltip-anchor">
                <DownloadButton />
              </span>
            </OverlayTooltip>
          ) : (
            <DownloadButton />
          ))}
      </div>
    </>
  );
};

export default memo(AudioPlayer);
